'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import type { SubscriptionTier } from '@/lib/subscription';
import { useAuth } from './AuthProvider';
import { PmfSurvey } from './PmfSurvey';

interface ReferralSummary {
  code: string;
  referralPath: string;
  invited: number;
  qualified: number;
  bonusCredits: number;
}

interface PostExportGrowthPanelProps {
  demo?: boolean;
  jobId: string;
  courseId?: string | null;
  fileName: string;
  acceptedCards: number;
  tier: SubscriptionTier;
  generationsRemaining: number;
  onStartAnother: () => void;
  onShowUpgrade: () => void;
  onReferralUpdated: () => void;
}

export function PostExportGrowthPanel({
  demo = false,
  jobId,
  courseId,
  fileName,
  acceptedCards,
  tier,
  generationsRemaining,
  onStartAnother,
  onShowUpgrade,
  onReferralUpdated,
}: PostExportGrowthPanelProps) {
  const { user, openSignIn } = useAuth();
  const posthog = usePostHog();
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFallback, setCopyFallback] = useState('');
  const capturedView = useRef(false);

  const loadSummary = useCallback(async () => {
    if (demo) {
      setSummary({ code: 'demo', referralPath: '/?ref=demo', invited: 0, qualified: 0, bonusCredits: 0 });
      return;
    }
    if (!user) return;
    const response = await fetch('/api/referrals', { cache: 'no-store' });
    if (!response.ok) return;
    setSummary(await response.json() as ReferralSummary);
  }, [user, demo]);

  useEffect(() => {
    if (capturedView.current) return;
    capturedView.current = true;
    posthog?.capture('post_export_growth_viewed', {
      job_id: jobId,
      course_id: courseId,
      accepted_cards: acceptedCards,
      current_tier: tier,
      generations_remaining: Number.isFinite(generationsRemaining) ? generationsRemaining : -1,
      signed_in: Boolean(user),
    });
  }, [acceptedCards, courseId, generationsRemaining, jobId, posthog, tier, user]);

  useEffect(() => {
    if (demo) { void loadSummary(); return; }
    if (!user) return;
    void fetch('/api/referrals/qualify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId }),
    })
      .then(async response => {
        const result = await response.json().catch(() => ({})) as { qualified?: boolean };
        if (result.qualified) onReferralUpdated();
        return loadSummary();
      })
      .catch(() => loadSummary());
  }, [jobId, loadSummary, onReferralUpdated, user, demo]);

  const startAnother = () => {
    posthog?.capture('post_export_next_lecture_clicked', {
      job_id: jobId,
      course_id: courseId,
      current_tier: tier,
    });
    onStartAnother();
  };

  const copyReferral = async () => {
    if (!summary) return;
    const url = `${window.location.origin}${summary.referralPath}`;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      setCopyFallback('');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
      posthog?.capture('referral_link_copied', {
        job_id: jobId,
        referral_code: summary.code,
        current_tier: tier,
      });
    } catch {
      setCopyFallback(url);
      posthog?.capture('referral_link_copy_failed', { job_id: jobId });
    }
  };

  const showUpgrade = () => {
    posthog?.capture('post_export_upgrade_clicked', {
      job_id: jobId,
      current_tier: tier,
      generations_remaining: Number.isFinite(generationsRemaining) ? generationsRemaining : -1,
    });
    onShowUpgrade();
  };

  return (
    <aside className="mt-6 overflow-hidden rounded-2xl border border-primary/25 bg-card text-left shadow-sm" aria-label="Next steps after export">
      <div className="border-b border-border bg-primary/5 px-5 py-4 sm:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Export complete</p>
        <h3 className="mt-1 font-serif text-xl font-semibold text-foreground">Keep the momentum from {fileName.replace(/\.[^.]+$/, '')}</h3>
        <p className="mt-1 text-sm text-muted">Your {acceptedCards} approved card{acceptedCards === 1 ? ' is' : 's are'} downloaded. Choose the next useful step.</p>
      </div>

      <div className="grid gap-3 p-5 sm:grid-cols-2 sm:p-6">
        <button
          type="button"
          onClick={startAnother}
          className="min-h-24 rounded-xl border border-border bg-surface p-4 text-left transition hover:border-primary"
        >
          <strong className="block text-sm text-foreground">Process the next lecture</strong>
          <span className="mt-1 block text-xs leading-5 text-muted">Start another deck while this material is fresh.</span>
        </button>
        {tier !== 'free' ? (
          <Link
            href="/account"
            onClick={() => posthog?.capture('post_export_account_clicked', { job_id: jobId, current_tier: tier })}
            className="min-h-24 rounded-xl border border-border bg-surface p-4 text-left transition hover:border-primary"
          >
            <strong className="block text-sm text-foreground">Manage your account</strong>
            <span className="mt-1 block text-xs leading-5 text-muted">Manage your plan and email preferences.</span>
          </Link>
        ) : (
          <button
            type="button"
            onClick={showUpgrade}
            className="min-h-24 rounded-xl border border-primary/40 bg-primary/5 p-4 text-left transition hover:border-primary"
          >
            <strong className="block text-sm text-primary">Keep more lectures moving</strong>
            <span className="mt-1 block text-xs leading-5 text-muted">
              Unlimited generation for your upcoming lectures.
            </span>
          </button>
        )}
      </div>

      <div className="border-t border-border px-5 py-5 sm:px-6">
        {copyFallback && <div className="mb-4">
          <p role="status" className="mb-2 text-xs text-muted">Automatic copying is unavailable. Select and copy your invitation link below.</p>
          <input aria-label="Invitation link" readOnly value={copyFallback} onFocus={event => event.currentTarget.select()}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
        </div>}
        {user ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <strong className="text-sm text-foreground">Give a lecture, get a lecture</strong>
              <p className="mt-1 max-w-xl text-xs leading-5 text-muted">
                Invite a classmate. After their first referred export, you each receive one bonus lecture credit.
                {summary?.bonusCredits ? ` You currently have ${summary.bonusCredits} bonus credit${summary.bonusCredits === 1 ? '' : 's'}.` : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={copyReferral}
              disabled={!summary}
              className="ui-button shrink-0 border border-primary text-primary"
            >
              {copied ? 'Invitation copied' : summary ? 'Copy invitation link' : 'Preparing link…'}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <strong className="text-sm text-foreground">Save your progress and invite classmates</strong>
              <p className="mt-1 text-xs leading-5 text-muted">Sign in to keep decks private in your library and unlock referral credits.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                posthog?.capture('post_export_sign_in_clicked', { job_id: jobId, accepted_cards: acceptedCards });
                void openSignIn(`/upload?job=${encodeURIComponent(jobId)}&file=${encodeURIComponent(fileName)}`);
              }}
              className="ui-button ui-button--primary shrink-0"
            >
              Sign in with Google
            </button>
          </div>
        )}
      </div>

      <div className="px-5 pb-5 sm:px-6 sm:pb-6">
        <PmfSurvey jobId={jobId} tier={tier} />
      </div>
    </aside>
  );
}
